import { useState } from 'react'
import cn from 'classnames'
import { NumberInput } from '@mantine/core'
import { ContentCard, Stack } from '@forwardflow/ui-v2'
import { CheckCircle02Icon } from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import {
   BUDGET_PRESETS,
   clampBudget,
   conversationsFor,
   MAX_BUDGET,
   MIN_BUDGET,
   splitBudget,
} from '../budget-presets'
import { DEV_DUMMY, useDevTools } from '../dev/devTools'
import { PaymentDetails } from './PaymentDetails'

/**
 * Step 3: the monthly call budget, then the card, on one screen.
 *
 * Three presets (Starter is the stored default) with the conversations they
 * buy, and a custom amount. The amount feeds the same `pricingData` the
 * payment form charges from, so the "Charged today" box below updates as
 * presets are tapped. The card form is the existing payment step, unchanged.
 */
export const BudgetAndCard = () => {
   const { onboardingData, setPricingData } = useOnboardingStoreV2()
   const { pricingData, isSubscriptionActivated } = onboardingData
   const maxSpend = pricingData?.maxSpend ?? 150
   const presetFor = BUDGET_PRESETS.find((p) => p.maxSpend === maxSpend)
   const [custom, setCustom] = useState(!presetFor)

   const applyBudget = (amount: number) =>
      setPricingData({ ...pricingData, ...splitBudget(clampBudget(amount)) })

   // Dev tools (hidden bar): a $200 budget, then let the card form fill.
   useDevTools({ fill: () => applyBudget(DEV_DUMMY.maxSpend) })

   return (
      <Stack gap={24}>
         {!isSubscriptionActivated && (
            <ContentCard
               className="space-y-4"
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3 }}
            >
               <Stack gap={4}>
                  <p className="text-white font-semibold text-lg">
                     How much should we spend on calls each month?
                  </p>
                  <p className="text-[#99A1AF] text-sm font-medium max-w-[640px]">
                     Calls are paid from a balance we top up to this amount
                     each cycle. Unused balance rolls over. Change it any time
                     from Billing.
                  </p>
               </Stack>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-3" role="radiogroup">
                  {BUDGET_PRESETS.map((p) => {
                     const selected = !custom && maxSpend === p.maxSpend
                     return (
                        <button
                           key={p.id}
                           type="button"
                           role="radio"
                           aria-checked={selected}
                           onClick={() => {
                              setCustom(false)
                              applyBudget(p.maxSpend)
                           }}
                           className={cn(
                              'text-left rounded-2xl border px-5 py-4 transition-all',
                              selected
                                 ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.10)]'
                                 : 'border-[#3f3e47] bg-white/[0.02] hover:border-white/30'
                           )}
                        >
                           <div className="flex items-start justify-between gap-2">
                              <p className="text-white font-semibold">{p.label}</p>
                              <span className={cn('transition-opacity', selected ? 'opacity-100' : 'opacity-0')}>
                                 <CheckCircle02Icon width={20} height={20} color="#A684FF" />
                              </span>
                           </div>
                           <p className="mt-1">
                              <span className="text-white font-semibold text-2xl">${p.maxSpend}</span>
                              <span className="text-white/50 text-sm"> /month</span>
                           </p>
                           <p className="text-[#99A1AF] text-sm mt-2">
                              About {conversationsFor(p.maxSpend)} lead conversations
                           </p>
                           <p className="text-white/40 text-xs mt-1">{p.blurb}</p>
                        </button>
                     )
                  })}
               </div>

               <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {custom ? (
                     <>
                        <NumberInput
                           min={MIN_BUDGET}
                           max={MAX_BUDGET}
                           step={50}
                           value={maxSpend}
                           prefix="$"
                           label="Custom monthly budget"
                           classNames={{
                              input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2',
                           }}
                           onChange={(value) => applyBudget(Number(value))}
                           data-testid="custom-budget"
                        />
                        <p className="text-[#99A1AF] text-sm sm:pt-6">
                           About {conversationsFor(maxSpend)} lead conversations a month
                        </p>
                     </>
                  ) : (
                     <button
                        type="button"
                        className="text-[#A684FF] text-sm font-medium hover:underline self-start"
                        onClick={() => setCustom(true)}
                     >
                        Set a custom amount
                     </button>
                  )}
               </div>
            </ContentCard>
         )}

         <PaymentDetails />
      </Stack>
   )
}
